/**
 * Raw design values of the Pyplan design system, copied in `designSystem.json`. Only semantic and
 * domain tokens may read them; components never do.
 */
export const primitives = {
  color: {
    base: { white: '#ffffff', black: '#000000' },
    blue: {
      50: '#f2f7ff',
      100: '#ddeaff',
      200: '#c1d7fe',
      300: '#99bdfd',
      400: '#689dfc',
      500: '#2d76fa',
      600: '#0f56d0',
      700: '#003a9d',
      800: '#012a72',
      900: '#00194c',
      950: '#0e152c'
    },
    celeste: {
      50: '#f3f7fb',
      100: '#e6ecf1',
      200: '#d6e2eb',
      300: '#becdd8',
      400: '#a3b4c1',
      500: '#8698a6',
      600: '#6b7d8b',
      700: '#52636f',
      800: '#3c4a54',
      900: '#283239',
      950: '#181e22'
    },
    teal: {
      50: '#ecfafb',
      100: '#daf0f2',
      200: '#bee2e5',
      300: '#97cdd2',
      400: '#75b4ba',
      500: '#579ba1',
      600: '#3d8188',
      700: '#27686e',
      800: '#175055',
      900: '#0e383b',
      950: '#0a2123'
    },
    yellow: {
      50: '#fff6e1',
      100: '#ffecbf',
      200: '#fbe2a4',
      300: '#ffd76f',
      400: '#eab80b',
      500: '#c59b0d',
      600: '#a17e0a',
      700: '#7f6300',
      800: '#5f4800',
      900: '#3f3003',
      950: '#261b01'
    },
    neutral: {
      50: '#f2f2f2',
      100: '#e9e9e9',
      200: '#dbdbdb',
      300: '#c6c6c6',
      400: '#adadad',
      500: '#919191',
      600: '#767676',
      700: '#5d5d5d',
      800: '#444444',
      900: '#2d2d2d',
      950: '#1a1a1a'
    },
    green: {
      50: '#e8f6f0',
      100: '#e2f0ea',
      200: '#cfe8dc',
      300: '#acdbc5',
      400: '#83c9a8',
      500: '#56b489',
      600: '#1f9d6b',
      700: '#0f7c53',
      800: '#075c3c',
      900: '#023d27',
      950: '#012415'
    },
    red: {
      50: '#fbebeb',
      100: '#f5dcdc',
      200: '#f8c2c1',
      300: '#f69e9b',
      400: '#ea7471',
      500: '#d64545',
      600: '#ba2e32',
      700: '#9c1a22',
      800: '#7c0d16',
      900: '#5a090f',
      950: '#38090a'
    },
    alphaWhite: { 8: '#ffffff14', 12: '#ffffff1f', 16: '#ffffff29', 20: '#ffffff33' },
    /** The design system's modal scrim, which it does not publish as a token. */
    scrim: 'rgba(14, 21, 44, 0.4)',
    /** Diagram node colors; the design system does not define them. */
    diagram: {
      variable: '#7AAFC7',
      datareading: '#7DB87A',
      outputnode: '#E88FAB',
      report: '#9E9E9E',
      button: '#BDBDBD',
      module: '#7A9BB5',
      index: '#9999FF',
      input: '#D4924C'
    }
  },
  font: {
    family: { sans: 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif' },
    weight: { regular: 400, medium: 500, semibold: 600 },
    style: {
      displayLg: { fontSize: 32, lineHeight: 40, fontWeight: 600, letterSpacing: '-0.01em' },
      headingXl: { fontSize: 24, lineHeight: 32, fontWeight: 600, letterSpacing: '-0.01em' },
      headingLg: { fontSize: 20, lineHeight: 28, fontWeight: 600 },
      headingMd: { fontSize: 16, lineHeight: 24, fontWeight: 600 },
      headingSm: { fontSize: 14, lineHeight: 20, fontWeight: 600 },
      bodyMdRegular: { fontSize: 14, lineHeight: 20, fontWeight: 400 },
      bodyMdMedium: { fontSize: 14, lineHeight: 20, fontWeight: 500 },
      bodySmRegular: { fontSize: 13, lineHeight: 18, fontWeight: 400 },
      bodySmMedium: { fontSize: 13, lineHeight: 18, fontWeight: 500 },
      captionRegular: { fontSize: 12, lineHeight: 16, fontWeight: 400 },
      captionMedium: { fontSize: 12, lineHeight: 16, fontWeight: 500 },
      labelButton: { fontSize: 14, lineHeight: 20, fontWeight: 500 },
      dataKpi: { fontSize: 28, lineHeight: 36, fontWeight: 600, letterSpacing: '-0.01em' },
      dataMd: { fontSize: 14, lineHeight: 20, fontWeight: 400 },
      dataSm: { fontSize: 13, lineHeight: 18, fontWeight: 400 }
    }
  },
  /** MUI spacing unit; the 4px grid is `theme.spacing(0.5)` steps. */
  spacing: 8,
  radius: { none: 0, full: 999 },
  borderWidth: { default: 1, strong: 2 },
  size: {
    icon: { sm: 16, md: 20, lg: 24 },
    control: { sm: 28, md: 36, lg: 44 },
    tableRow: { compact: 32, default: 40 }
  },
  shadow: {
    elevation: {
      1: '0 1px 3px 0 rgba(14,21,44,0.06)',
      2: '0 4px 12px 0 rgba(14,21,44,0.10)',
      3: '0 12px 32px 0 rgba(14,21,44,0.16)'
    }
  },
  motion: {
    duration: { fast: 120, base: 200, slow: 320 },
    easing: {
      standard: 'cubic-bezier(0.2, 0, 0, 1)',
      emphasized: 'cubic-bezier(0.3, 0, 0, 1)'
    }
  }
} as const
